import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './LoanDataDisplay.scss';

interface OrganizationLoanReportItem {
  serialNumber: number;
  date: string | null;
  branch: string;
  loanNumber: string;
  customerName: string;
  loanType: string;
  grossWeight: number;
  netWeight: number;
  loanAmount: number;
  documentCharge: number;
  gst: number;
  scheme: string;
  interestPercentage: number | null;
  online: number;
  offline: number;
  loanAverage: number;
  remarks: string;
}

interface OrganizationLoanReportProps {
  data: OrganizationLoanReportItem[];
}

const formatDate = (val: string | null) => {
  if (val === null || val === undefined) return '-';
  const d = new Date(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatCurrency = (val: number) => `₹${(val ?? 0).toLocaleString('en-IN')}`;

// The scheme column carries the rate agreed on the loan alongside the scheme's name
const formatScheme = (scheme: string, record: OrganizationLoanReportItem) => {
  if (scheme == null || scheme.length === 0) return '-';
  if (record.interestPercentage == null) return scheme;
  return `${scheme} (${record.interestPercentage}%)`;
};

const LOAN_TYPE_LABEL: Record<string, string> = {
  direct: 'Direct',
  takeover: 'Takeover',
  repledge: 'Repledge',
};

const columns: ColumnsType<OrganizationLoanReportItem> = [
  {
    title: 'S.No',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    width: 60,
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    width: 120,
    className: 'date-cell',
    render: formatDate,
  },
  {
    title: 'Branch',
    dataIndex: 'branch',
    key: 'branch',
  },
  {
    title: 'Loan Number',
    dataIndex: 'loanNumber',
    key: 'loanNumber',
    render: (val: string) => <strong>{val}</strong>,
  },
  {
    title: 'Customer Name',
    dataIndex: 'customerName',
    key: 'customerName',
  },
  {
    title: 'Loan Type',
    dataIndex: 'loanType',
    key: 'loanType',
    render: (val: string) => LOAN_TYPE_LABEL[val] ?? val,
  },
  {
    title: 'G.W (g)',
    dataIndex: 'grossWeight',
    key: 'grossWeight',
    render: (val: number) => `${(val ?? 0).toFixed(3)} g`,
  },
  {
    title: 'N.W (g)',
    dataIndex: 'netWeight',
    key: 'netWeight',
    render: (val: number) => `${(val ?? 0).toFixed(3)} g`,
  },
  {
    title: 'Loan Amount',
    dataIndex: 'loanAmount',
    key: 'loanAmount',
    render: formatCurrency,
  },
  {
    title: 'DC',
    dataIndex: 'documentCharge',
    key: 'documentCharge',
    render: formatCurrency,
  },
  {
    title: 'GST',
    dataIndex: 'gst',
    key: 'gst',
    render: formatCurrency,
  },
  {
    title: 'Scheme',
    dataIndex: 'scheme',
    key: 'scheme',
    render: formatScheme,
  },
  {
    title: 'Online',
    dataIndex: 'online',
    key: 'online',
    render: formatCurrency,
  },
  {
    title: 'Offline',
    dataIndex: 'offline',
    key: 'offline',
    render: formatCurrency,
  },
  {
    title: 'Loan Average (₹/g)',
    dataIndex: 'loanAverage',
    key: 'loanAverage',
    render: formatCurrency,
  },
  {
    title: 'Remarks',
    dataIndex: 'remarks',
    key: 'remarks',
  },
];

const OrganizationLoanReportDisplay: React.FC<OrganizationLoanReportProps> = ({ data }) => {
  // Totals cover the whole report, not just the visible page.
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          grossWeight: acc.grossWeight + (item.grossWeight ?? 0),
          netWeight: acc.netWeight + (item.netWeight ?? 0),
          loanAmount: acc.loanAmount + (item.loanAmount ?? 0),
          documentCharge: acc.documentCharge + (item.documentCharge ?? 0),
          gst: acc.gst + (item.gst ?? 0),
          online: acc.online + (item.online ?? 0),
          offline: acc.offline + (item.offline ?? 0),
        }),
        {
          grossWeight: 0,
          netWeight: 0,
          loanAmount: 0,
          documentCharge: 0,
          gst: 0,
          online: 0,
          offline: 0,
        },
      ),
    [data],
  );

  // Loan average is a rate, so the total row shows the portfolio-wide rate rather than a sum.
  const overallAverage =
    totals.netWeight > 0 ? totals.loanAmount / totals.netWeight : 0;

  return (
    <div className="loan-data-container">
      <h2>Organization Loan Report</h2>
      <Table
        columns={columns}
        dataSource={data.map((item) => ({
          ...item,
          key: `${item.loanNumber}-${item.serialNumber}`,
        }))}
        rowKey="key"
        pagination={{ pageSize: 20, showTotal: (t) => `${t} records` }}
        scroll={{ x: true }}
        bordered
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className="totals-row">
              <Table.Summary.Cell index={0} colSpan={6}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                <strong>{totals.grossWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>{totals.netWeight.toFixed(3)} g</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8}>
                <strong>{formatCurrency(totals.loanAmount)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9}>
                <strong>{formatCurrency(totals.documentCharge)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{formatCurrency(totals.gst)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11}>-</Table.Summary.Cell>
              <Table.Summary.Cell index={12}>
                <strong>{formatCurrency(totals.online)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13}>
                <strong>{formatCurrency(totals.offline)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={14}>
                <strong>₹{overallAverage.toFixed(2)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={15}>-</Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default OrganizationLoanReportDisplay;
